// 017 — Protocolo Quirúrgico (Anexo 1 MSP, SNS-MSP/HCU-form. 017/2021). Transcripción campo por campo del PDF oficial
// (páginas 77-78; spec en docs/hce-anexos/017-protocolo-quirurgico.md), bloques A-J. El bloque A lo resuelve
// `EncabezadoMspCampos`; el resto está aquí.
//
// Equivalencia funcional, no facsímil. Decisiones:
// - I (diagrama del procedimiento) es un recuadro en blanco para dibujar a mano: no se implementa como campo (no hay motor de
//   dibujo libre); queda para el papel/Documentos si el cirujano lo necesita.
// - J: FIRMA y SELLO no son campos (la firma la resuelve el PDF firmado); el número de documento del sello sí se captura.
// - E: casillas independientes (el papel no dice que sean excluyentes; una anestesia puede combinarse). "Otros" no tiene
//   espacio de texto en el papel.
// - F: la fecha de operación (DIA/MES/AÑO en tres celdas) es un solo campo de fecha.
// - "Patólogo que reporta" (H) es un rótulo suelto en el papel: se captura como texto opcional propio.
// - FHIR: `Procedure` completed con el "Realizado" (como el 017 de Questionnaire) y una `Condition` por diagnóstico pre y
//   postoperatorio con texto (como 018A/038); sin `verificationStatus` (el papel no tiene PRE/DEF). Todo lo demás va como
//   `Par[]` en el PDF firmado.
import type { Encounter, Procedure, Reference, Patient } from '@medplum/fhirtypes';
import { conCodigoCie, OPCIONES_SI_NO, paresDeCampos, valoresVacios, type CampoSimple, type Valores } from './camposSimplesDatos';
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';
import { estadoInicialTablaConColumnas, resumenFilaTablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';

export const NUM_DIAGNOSTICOS_017 = 3;

// ---------- C. Procedimiento ----------

export const CAMPOS_PROCEDIMIENTO_017: readonly CampoSimple[] = [
  {
    clave: 'tipo',
    etiqueta: 'Tipo de procedimiento',
    tipo: 'select',
    opciones: [
      { valor: 'electiva', etiqueta: 'Electiva' },
      { valor: 'emergencia', etiqueta: 'Emergencia' },
      { valor: 'urgencia', etiqueta: 'Urgencia' },
    ],
  },
  { clave: 'proyectado', etiqueta: 'Proyectado', tipo: 'area' },
  { clave: 'realizado', etiqueta: 'Realizado', tipo: 'area' },
];

// ---------- D. Integrantes del equipo quirúrgico ----------

export const CAMPOS_EQUIPO_017: readonly CampoSimple[] = [
  { clave: 'cirujano1', etiqueta: 'Cirujano 1' },
  { clave: 'instrumentista', etiqueta: 'Instrumentista' },
  { clave: 'cirujano2', etiqueta: 'Cirujano 2' },
  { clave: 'circulante', etiqueta: 'Circulante' },
  { clave: 'primerAyudante', etiqueta: 'Primer Ayudante' },
  { clave: 'anestesiologo', etiqueta: 'Anestesiólogo/a' },
  { clave: 'segundoAyudante', etiqueta: 'Segundo Ayudante' },
  { clave: 'ayudanteAnestesia', etiqueta: 'Ayudante Anestesia' },
  { clave: 'tercerAyudante', etiqueta: 'Tercer Ayudante' },
  { clave: 'otros', etiqueta: 'Otros' },
];

// ---------- E. Tipo de anestesia ----------

export const CAMPOS_ANESTESIA_017: readonly CampoSimple[] = [
  { clave: 'general', etiqueta: 'General', tipo: 'casilla' },
  { clave: 'regional', etiqueta: 'Regional', tipo: 'casilla' },
  { clave: 'sedacion', etiqueta: 'Sedación', tipo: 'casilla' },
  { clave: 'otros', etiqueta: 'Otros', tipo: 'casilla' },
];

// ---------- F. Tiempos quirúrgicos ----------

export const CAMPOS_TIEMPOS_017: readonly CampoSimple[] = [
  { clave: 'fecha', etiqueta: 'Fecha de operación', tipo: 'fecha' },
  { clave: 'horaInicio', etiqueta: 'Hora de inicio', tipo: 'hora' },
  { clave: 'horaTerminacion', etiqueta: 'Hora de terminación', tipo: 'hora' },
  { clave: 'dieresis', etiqueta: 'Dieresis', tipo: 'area' },
  { clave: 'exposicionExploracion', etiqueta: 'Exposición y Exploración', tipo: 'area' },
  { clave: 'hallazgos', etiqueta: 'Hallazgos Quirúrgicos', tipo: 'area' },
  { clave: 'procedimientoQuirurgico', etiqueta: 'Procedimiento Quirúrgico', tipo: 'area' },
];

// ---------- G. Complicaciones ----------

export const CAMPOS_COMPLICACIONES_017: readonly CampoSimple[] = [
  { clave: 'complicaciones', etiqueta: 'Complicaciones del procedimiento quirúrgico', tipo: 'area' },
  { clave: 'perdidaSanguineaMl', etiqueta: 'Pérdida sanguínea total (ml)', tipo: 'numero' },
  { clave: 'sangradoAproximadoMl', etiqueta: 'Sangrado aproximado (ml)', tipo: 'numero' },
  { clave: 'materialProtesico', etiqueta: 'Uso de material protésico', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'descripcionMaterial', etiqueta: 'Descripción' },
];

// ---------- H. Exámenes histopatológicos ----------

export const CAMPOS_HISTOPATOLOGICOS_017: readonly CampoSimple[] = [
  { clave: 'transquirurgico', etiqueta: 'Transquirúrgico' },
  { clave: 'biopsiaCongelacion', etiqueta: 'Biopsia por congelación', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'resultadoBiopsia', etiqueta: 'Resultado' },
  { clave: 'patologo', etiqueta: 'Patólogo que reporta' },
  { clave: 'histopatologico', etiqueta: 'Histopatológico', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'muestra', etiqueta: 'Muestra' },
];

// ---------- J. Datos del profesional responsable (4 filas) ----------

export const FILAS_PROFESIONALES_017: readonly FilaTablaConColumnas[] = [1, 2, 3, 4].map((n) => ({ clave: `p${n}`, etiqueta: String(n) }));
export const COLUMNAS_PROFESIONALES_017: readonly ColumnaTablaConColumnas[] = [
  { clave: 'nombre', etiqueta: 'Nombre y apellidos' },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
  { clave: 'documento', etiqueta: 'Número de documento de identificación' },
];

// ---------- Estado ----------

export interface Estado017 {
  readonly diagnosticosPre: readonly DiagnosticoCie10Valor[];
  readonly diagnosticosPost: readonly DiagnosticoCie10Valor[];
  readonly procedimiento: Valores;
  readonly equipo: Valores;
  readonly anestesia: Valores;
  readonly tiempos: Valores;
  readonly complicaciones: Valores;
  readonly histopatologicos: Valores;
  readonly profesionales: EstadoTablaConColumnas;
}

const diagnosticosVacios = (): DiagnosticoCie10Valor[] => Array.from({ length: NUM_DIAGNOSTICOS_017 }, () => ({ texto: '', cie10: null }));

export function estadoInicial017(): Estado017 {
  return {
    diagnosticosPre: diagnosticosVacios(),
    diagnosticosPost: diagnosticosVacios(),
    procedimiento: valoresVacios(CAMPOS_PROCEDIMIENTO_017),
    equipo: valoresVacios(CAMPOS_EQUIPO_017),
    anestesia: valoresVacios(CAMPOS_ANESTESIA_017),
    tiempos: valoresVacios(CAMPOS_TIEMPOS_017),
    complicaciones: valoresVacios(CAMPOS_COMPLICACIONES_017),
    histopatologicos: valoresVacios(CAMPOS_HISTOPATOLOGICOS_017),
    profesionales: estadoInicialTablaConColumnas(FILAS_PROFESIONALES_017, COLUMNAS_PROFESIONALES_017),
  };
}

/** Diagnósticos con texto (pre y postoperatorios), en el orden del papel — uno por `Condition`. */
export function diagnosticosConTexto017(e: Estado017): DiagnosticoCie10Valor[] {
  return [...e.diagnosticosPre, ...e.diagnosticosPost].filter((d) => d.texto.trim() !== '');
}

/** `Procedure` del "Realizado" (nada si no se escribió); fecha/hora de inicio si están, si no el momento de guardar. */
export function construirProcedure017(e: Estado017, subject: Reference<Patient>, encounter: Reference<Encounter> | undefined, ahora: Date): Procedure | undefined {
  const realizado = (e.procedimiento.realizado ?? '').trim();
  if (realizado === '') return undefined;
  const { fecha, horaInicio, procedimientoQuirurgico, hallazgos } = e.tiempos;
  const inicio = fecha ? new Date(`${fecha}T${horaInicio || '00:00'}:00`) : ahora;
  const notas = [
    procedimientoQuirurgico?.trim() && `Técnica: ${procedimientoQuirurgico.trim()}`,
    hallazgos?.trim() && `Hallazgos: ${hallazgos.trim()}`,
    e.complicaciones.complicaciones?.trim() && `Complicaciones: ${e.complicaciones.complicaciones.trim()}`,
  ].filter((n): n is string => !!n);
  return {
    resourceType: 'Procedure',
    status: 'completed',
    code: { text: realizado },
    subject,
    encounter,
    performedDateTime: (Number.isNaN(inicio.getTime()) ? ahora : inicio).toISOString(),
    note: notas.length > 0 ? notas.map((text) => ({ text })) : undefined,
  };
}

/** Serializa el formulario a `Par[]` para `onGuardado` (PDF firmado) — filtra respuestas vacías. */
export function paresDe017(t: Traducir, e: Estado017): Par[] {
  const p = (clave: string, campos: readonly CampoSimple[], valores: Valores): Par[] => paresDeCampos(campos, valores, `${t(`formularios.f017.${clave}`)} — `);
  const dx = (clave: string, lista: readonly DiagnosticoCie10Valor[]): Par[] => lista.map((d, i) => ({ pregunta: `${t(`formularios.f017.${clave}`)} ${i + 1}`, respuesta: conCodigoCie(d) }));
  const pares: Par[] = [
    ...dx('preoperatorio', e.diagnosticosPre),
    ...dx('postoperatorio', e.diagnosticosPost),
    ...paresDeCampos(CAMPOS_PROCEDIMIENTO_017, e.procedimiento),
    ...p('equipoTitulo', CAMPOS_EQUIPO_017, e.equipo),
    ...p('anestesiaTitulo', CAMPOS_ANESTESIA_017, e.anestesia),
    ...paresDeCampos(CAMPOS_TIEMPOS_017, e.tiempos),
    ...paresDeCampos(CAMPOS_COMPLICACIONES_017, e.complicaciones),
    ...p('histopatologicosTitulo', CAMPOS_HISTOPATOLOGICOS_017, e.histopatologicos),
    ...FILAS_PROFESIONALES_017.map((f) => ({ pregunta: `${t('formularios.f017.profesionalesTitulo')} — ${f.etiqueta}`, respuesta: resumenFilaTablaConColumnas(e.profesionales, f.clave, COLUMNAS_PROFESIONALES_017) })),
  ];
  return pares.filter((x) => x.respuesta.trim() !== '');
}
